/*
 * Revenue / cost figures of a finance job, exactly as the Finance Jobs table
 * shows them (REVENUE / COST columns). Shared so other screens that list
 * jobs (e.g. the Incoming Invoices job picker) show the same numbers.
 */

const normalizeCurrencyCode = (currency) => {
    const code = String(currency || "EUR")
        .trim()
        .toUpperCase();
    if (code === "EURO") return "EUR";
    if (code === "TL") return "TRY";
    return code;
};

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const firstDefinedNumber = (...values) => {
    const value = values.find(
        (item) => item !== undefined && item !== null && item !== "",
    );

    return toNumber(value);
};

export const getCurrency = (job) =>
    normalizeCurrencyCode(
        job?.sellingRateCurrency ||
        job?.selling_rate_currency ||
        job?.currency ||
        job?.invoiceCurrency ||
        job?.invoice_currency ||
        job?.financialSummary?.currency ||
        job?.financialSummary?.sellingRateCurrency ||
        "EUR",
    );

export const getCostCurrency = (job) => {
    const items =
        [
            job?.invoiceItems,
            job?.invoice_items,
            job?.cargoDetails,
            job?.cargo_details,
        ].find(Array.isArray) || [];
    const itemWithCurrency = items.find(
        (item) => item?.costCurrency || item?.cost_currency,
    );
    const itemCurrency =
        itemWithCurrency?.costCurrency || itemWithCurrency?.cost_currency;

    return normalizeCurrencyCode(
        job?.costCurrency ||
        job?.cost_currency ||
        job?.totalCostCurrency ||
        job?.total_cost_currency ||
        itemCurrency ||
        job?.financialSummary?.costCurrency ||
        job?.financialSummary?.totalCurrency ||
        "EUR",
    );
};

export const getRevenue = (job) =>
    firstDefinedNumber(
        job?.invoiceSalesAmount,
        job?.invoice_sales_amount,
        job?.salesAmount,
        job?.sales_amount,
        job?.customerInvoiceAmount,
        job?.customer_invoice_amount,
        job?.actualRevenueTotal,
        job?.actual_revenue_total,
        job?.totalAmount,
        job?.total_amount,
        job?.amount,
        job?.financialSummary?.totalSellingRate,
        job?.financial_summary?.total_selling_rate,
    );

export const getBuyingRate = (job) => {
    return firstDefinedNumber(
        job?.buyingRate,
        job?.buying_rate,

        job?.financialSummary?.buyingRate,
        job?.financial_summary?.buying_rate,

        job?.freightCharges?.buyingRate,
        job?.freight_charges?.buying_rate,
    );
};

export const getOperationalCost = (job) => {
    const summary = job?.financialSummary || job?.financial_summary || {};

    const freightCharges = job?.freightCharges || job?.freight_charges || {};

    const explicitOperationalCost = firstDefinedNumber(
        job?.operationalCost,
        job?.operational_cost,
        summary?.operationalCost,
        summary?.operational_cost,
    );

    if (explicitOperationalCost > 0) {
        return explicitOperationalCost;
    }

    const roadCost =
        firstDefinedNumber(summary?.shipmentCostTotal) +
        firstDefinedNumber(summary?.convertedLinehaulCost) +
        firstDefinedNumber(summary?.convertedHandlingCost);

    const oceanCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.oceanFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const airCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.airFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const freightChargeCost =
        firstDefinedNumber(freightCharges?.pickupTotal) +
        firstDefinedNumber(freightCharges?.othc?.total) +
        firstDefinedNumber(freightCharges?.oceanFreight?.total) +
        firstDefinedNumber(freightCharges?.airFreight?.total) +
        firstDefinedNumber(freightCharges?.dthc?.total) +
        firstDefinedNumber(freightCharges?.handling?.total) +
        firstDefinedNumber(freightCharges?.deliveryTotal);

    if (roadCost > 0) return roadCost;
    if (oceanCost > 0) return oceanCost;
    if (airCost > 0) return airCost;
    if (freightChargeCost > 0) {
        return freightChargeCost;
    }

    return 0;
};

export const getCost = (job) => {
    const explicitTotalCost = firstDefinedNumber(
        job?.actualCostTotal,
        job?.actual_cost_total,
        job?.totalCost,
        job?.total_cost,
        job?.financialSummary?.totalCost,
        job?.financial_summary?.total_cost,
    );

    const includesBuyingRate =
        job?.totalCostIncludesBuyingRate === true ||
        job?.total_cost_includes_buying_rate === true ||
        job?.financialSummary?.totalCostIncludesBuyingRate === true ||
        job?.financial_summary?.total_cost_includes_buying_rate === true;

    const invoiceItemCost = Array.isArray(job?.invoiceItems)
        ? job.invoiceItems.reduce(
            (sum, item) =>
                sum +
                firstDefinedNumber(
                    item?.totalCost,
                    item?.cost,
                    item?.unitCost,
                    item?.buyingRate,
                ),
            0,
        )
        : 0;

    if (explicitTotalCost > 0) {
        return includesBuyingRate
            ? explicitTotalCost
            : explicitTotalCost + getBuyingRate(job);
    }

    if (invoiceItemCost > 0) {
        return invoiceItemCost;
    }

    return getOperationalCost(job) + getBuyingRate(job);
};
